import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import axios, { AxiosRequestConfig } from "axios";
import { DateTime } from "luxon";

import getErrorDetails from "./helpers/getErrorDetails.js";
import {
  addPaginationAndSort,
  getPaginationInfoFromHeaders,
  PaginatedList,
} from "./helpers/pagination";
import paramsSerializer from "./helpers/paramsSerializer";
import { isUUID, UUID } from "./../utils";
import { STATUS_NEW_PAYMENT } from "../constants/transactionStatuses";
import Payment from "../models/Payment";

export function formatApiDate(date: DateTime) {
  return date.toFormat("yyyyLLddHHmmss");
}

export async function getPayments(
  qsParams: {
    statuses: string[] | null;
    startDate: string | null;
    endDate: string | null;
    minAmount: number | null;
    maxAmount: number | null;
    query: string | null;
    page: number;
    order: any;
    orderBy: any;
    limit: number;
  },
  options?: AxiosRequestConfig
): Promise<PaginatedList<Payment>> {
  const {
    statuses,
    startDate,
    endDate,
    minAmount,
    maxAmount,
    query,
  } = qsParams;

  const params: Record<string, any> = {
    ...addPaginationAndSort<Payment>(qsParams),
  };

  if (!isEmpty(statuses)) {
    params.status = statuses;
  }

  if (startDate) {
    const luxonStartDate = DateTime.fromISO(startDate).startOf("day");
    params["createdAt[gte]"] = formatApiDate(luxonStartDate);
  }
  if (endDate) {
    const luxonEndDate = DateTime.fromISO(endDate).endOf("day");
    params["createdAt[lte]"] = formatApiDate(luxonEndDate);
  }

  if (!isNil(minAmount)) {
    params["amount[gte]"] = minAmount;
  }
  if (!isNil(maxAmount)) {
    params["amount[lte]"] = maxAmount;
  }

  if (!isEmpty(query)) {
    if (isUUID(query as string)) {
      params["id"] = query;
    } else {
      params["uniqueReference"] = query;
    }
  }

  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/payments",
      params,
      paramsSerializer,
    });
    const { data = {}, headers: responseHeaders = {} } = response;

    const {
      totalItems,
      currentPage,
      currentLimit,
    } = getPaginationInfoFromHeaders(responseHeaders);

    return {
      list: data || [],
      totalItems: totalItems,
      currentPage,
      currentLimit,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getPayment(
  { id }: { id: UUID },
  options?: AxiosRequestConfig
): Promise<Payment> {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/payments/${id}`,
    });
    const { data = {} } = response;
    return {
      // entity type so that transaction details page can distinguish
      // between incomplete list item and full entity
      __type: "getPayment",
      ...data,
      statusUpdates: [
        {
          createdAt: data.createdAt,
          newStatus: STATUS_NEW_PAYMENT,
        },
        ...(data.statusUpdates || []),
      ],
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}
